import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Label } from '@/components/ui/label';
import { Mail, Phone, Clock, User, MessageSquare, Eye, Trash2, Send } from 'lucide-react';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';
import { he } from 'date-fns/locale';

interface ContactRequest {
  id: string;
  user_id: string | null;
  full_name: string;
  phone: string;
  email: string;
  subject: string;
  inquiry_type: string | null;
  message: string;
  status: string;
  created_at: string;
  updated_at: string;
}

import ContactRequestDetailModal, { ContactRequestFull } from './ContactRequestDetailModal';

const ContactRequestsModule = () => {
  const [contactRequests, setContactRequests] = useState<ContactRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedRequest, setSelectedRequest] = useState<ContactRequestFull | null>(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [adminResponse, setAdminResponse] = useState('');
  const [responseLoading, setResponseLoading] = useState(false);
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchContactRequests();
  }, []);

  const fetchContactRequests = async () => {
    try {
      setLoading(true);
      // Use secure function that returns full email/phone for admins
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');
      
      const { data, error } = await supabase
        .rpc('get_admin_contact_requests_secure', {
          requesting_user_id: user.id
        });

      if (error) throw error;
      setContactRequests(data || []);
    } catch (error) {
      console.error('Error fetching contact requests:', error);
      toast.error('שגיאה בטעינת פניות הקשר');
    } finally {
      setLoading(false);
    }
  };

  const handleViewRequest = (request: ContactRequest) => {
    const full: ContactRequestFull = {
      id: request.id,
      user_id: request.user_id,
      full_name: request.full_name,
      phone: request.phone,
      email: request.email,
      subject: request.subject,
      inquiry_type: request.inquiry_type,
      message: request.message,
      status: request.status,
      created_at: request.created_at,
      updated_at: request.updated_at,
    };

    setSelectedRequest(full);
    setIsDetailOpen(true);
  };
  const updateRequestStatus = async (requestId: string, newStatus: string) => {
    try {
      const { error } = await supabase
        .from('contact_requests')
        .update({
          status: newStatus,
          updated_at: new Date().toISOString(),
        })
        .eq('id', requestId);

      if (error) throw error;

      setContactRequests(prev =>
        prev.map(req =>
          req.id === requestId
            ? { ...req, status: newStatus, updated_at: new Date().toISOString() }
            : req,
        ),
      );

      if (selectedRequest && selectedRequest.id === requestId) {
        setSelectedRequest(prev => (prev ? { ...prev, status: newStatus } as ContactRequestFull : null));
      }

      toast.success('סטטוס הפנייה עודכן בהצלחה');
    } catch (error) {
      console.error('Error updating request status:', error);
      toast.error('שגיאה בעדכון סטטוס הפנייה');
    }
  };
  const handleDeleteRequest = async (requestId: string) => {
    try {
      const { error } = await supabase.rpc('delete_contact_request_cascade', {
        request_id_param: requestId,
      });

      if (error) throw error;

      toast.success('פנייה נמחקה בהצלחה');
      fetchContactRequests();
    } catch (error: any) {
      console.error('Error deleting contact request:', error);
      toast.error(`שגיאה במחיקת הפנייה: ${error.message}`);
    }
  };

  const handleSendResponse = async (requestId: string) => {
    if (!adminResponse.trim()) {
      toast.error('אנא הזן תגובה');
      return;
    }

    setResponseLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('send-contact-response', {
        body: {
          contactRequestId: requestId,
          responseMessage: adminResponse.trim(),
          adminName: 'צוות Excess Market',
        },
      });

      if (error) throw error;

      if (data?.success) {
        toast.success('התגובה נשלחה בהצלחה לאימייל הלקוח');
        setAdminResponse('');
        // Refresh list to show updated status
        fetchContactRequests();
      } else {
        throw new Error(data?.error || 'Unknown error occurred');
      }
    } catch (error: any) {
      console.error('Error sending response:', error);
      toast.error(`שגיאה בשליחת התגובה: ${error.message}`);
    } finally {
      setResponseLoading(false);
    }
  };

  const getStatusBadge = (status: string) => {
    const statusMap = {
      pending: { label: 'ממתין', variant: 'secondary' as const },
      in_progress: { label: 'בטיפול', variant: 'default' as const },
      resolved: { label: 'נפתר', variant: 'outline' as const },
      closed: { label: 'סגור', variant: 'destructive' as const },
    };

    const statusInfo = statusMap[status as keyof typeof statusMap] || statusMap.pending;
    return <Badge variant={statusInfo.variant}>{statusInfo.label}</Badge>;
  };

  const getInquiryTypeLabel = (type: string | null) => {
    const typeMap = {
      supplier: 'הצטרפות כספק',
      buyer: 'שאלות קנייה',
      technical: 'תמיכה טכנית',
      business: 'פיתוח עסקי',
      other: 'אחר',
    };
    return typeMap[type as keyof typeof typeMap] || 'אחר';
  };

  const filteredRequests = contactRequests.filter(request => {
    const matchesStatus = filterStatus === 'all' || request.status === filterStatus;
    const matchesSearch =
      searchTerm === '' ||
      request.full_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      request.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      request.subject.toLowerCase().includes(searchTerm.toLowerCase());

    return matchesStatus && matchesSearch;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mb-4"></div>
          <p>טוען פניות קשר...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" dir="rtl">
      <div className="flex flex-col sm:flex-row gap-4 justify-between items-start mb-6">
        <div className="flex-1 w-full sm:w-auto">
          <h3 className="text-lg sm:text-xl font-semibold text-right">פניות קשר</h3>
          <p className="text-xs sm:text-sm text-muted-foreground text-right">
            צפייה וניהול פניות הקשר מהאתר
          </p>
        </div>

        <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
          <Input
            placeholder="חיפוש לפי שם, אימייל או נושא..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className="flex-1 sm:w-64 text-right"
            dir="rtl"
          />
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-full sm:w-32">
              <SelectValue placeholder="סטטוס" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">הכל</SelectItem>
              <SelectItem value="pending">ממתין</SelectItem>
              <SelectItem value="in_progress">בטיפול</SelectItem>
              <SelectItem value="resolved">נפתר</SelectItem>
              <SelectItem value="closed">סגור</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-primary text-right">
              {contactRequests.length}
            </div>
            <p className="text-sm text-muted-foreground text-right">סך הכל פניות</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-yellow-600 text-right">
              {contactRequests.filter(r => r.status === 'pending').length}
            </div>
            <p className="text-sm text-muted-foreground text-right">ממתינות לטיפול</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-blue-600 text-right">
              {contactRequests.filter(r => r.status === 'in_progress').length}
            </div>
            <p className="text-sm text-muted-foreground text-right">בטיפול</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-green-600 text-right">
              {contactRequests.filter(r => r.status === 'resolved').length}
            </div>
            <p className="text-sm text-muted-foreground text-right">נפתרו</p>
          </CardContent>
        </Card>
      </div>

      {/* Requests Table - rebuilt for clean RTL alignment */}
      <Card>
        <CardContent className="p-0">
          {filteredRequests.length === 0 ? (
            <div className="py-10 text-center text-muted-foreground">לא נמצאו פניות</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-right">
                <thead className="border-b bg-muted/40">
                  <tr>
                    <th className="px-4 py-3 font-medium">שם מלא</th>
                    <th className="px-4 py-3 font-medium">אימייל</th>
                    <th className="px-4 py-3 font-medium">טלפון</th>
                    <th className="px-4 py-3 font-medium">נושא</th>
                    <th className="px-4 py-3 font-medium">סוג פנייה</th>
                    <th className="px-4 py-3 font-medium">סטטוס</th>
                    <th className="px-4 py-3 font-medium">נוצר</th>
                    <th className="px-4 py-3 font-medium text-center">פעולות</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRequests.map(request => (
                    <tr key={request.id} className="border-b last:border-0 hover:bg-muted/40">
                      <td className="px-4 py-3 whitespace-nowrap">{request.full_name}</td>
                      <td className="px-4 py-3" dir="ltr">{request.email}</td>
                      <td className="px-4 py-3" dir="ltr">{request.phone}</td>
                      <td className="px-4 py-3 max-w-xs">
                        <div className="truncate font-medium">{request.subject}</div>
                        <div className="text-xs text-muted-foreground truncate">
                          {request.message}
                        </div>
                      </td>
                      <td className="px-4 py-3">{getInquiryTypeLabel(request.inquiry_type)}</td>
                      <td className="px-4 py-3">{getStatusBadge(request.status)}</td>
                      <td className="px-4 py-3 text-sm text-muted-foreground">
                        {formatDistanceToNow(new Date(request.created_at), {
                          locale: he,
                          addSuffix: true,
                        })}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-center gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            className="flex items-center gap-1"
                            onClick={() => handleViewRequest(request)}
                          >
                            <Eye className="h-3 w-3" />
                            צפייה
                          </Button>

                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button variant="destructive" size="sm">
                                <Trash2 className="h-3 w-3" />
                                מחק
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent dir="rtl">
                              <AlertDialogHeader>
                                <AlertDialogTitle>האם אתה בטוח?</AlertDialogTitle>
                                <AlertDialogDescription>
                                  פעולה זו תמחק את הפנייה לצמיתות. לא ניתן לבטל פעולה זו.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>ביטול</AlertDialogCancel>
                                <AlertDialogAction
                                  onClick={() => handleDeleteRequest(request.id)}
                                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                >
                                  מחק
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Central detail dialog rebuilt to be stable and fully RTL */}
      <ContactRequestDetailModal
        request={selectedRequest}
        isOpen={isDetailOpen}
        onClose={() => {
          setIsDetailOpen(false);
          setSelectedRequest(null);
          setAdminResponse('');
        }}
        onStatusChange={updateRequestStatus}
        onAfterResponse={fetchContactRequests}
      />
    </div>
  );
};

export default ContactRequestsModule;